import type { ReactNode } from 'react';
import { Typography } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { RangeKey, StatusFilterKey } from '../../../types/provider';

const { Text } = Typography;

interface Props {
  range: RangeKey;
  statusFilter: StatusFilterKey;
}

const STATUS_ICON_CONFIG: Record<StatusFilterKey, { icon: ReactNode; color: string }> = {
  ALL:                          { icon: <CalendarOutlined />,    color: '#3390ec' },
  PENDING:                      { icon: <ClockCircleOutlined />, color: '#faad14' },
  CONFIRMED:                    { icon: <CheckCircleOutlined />, color: '#1677ff' },
  COMPLETED:                    { icon: <CheckCircleOutlined />, color: '#1f8b4c' },
  CANCELED_BY_USER:             { icon: <CloseCircleOutlined />, color: '#ff4d4f' },
  CANCELED_BY_SERVICE_PROVIDER: { icon: <CloseCircleOutlined />, color: '#ff4d4f' },
};

export default function BookingEmptyState({ range, statusFilter }: Props) {
  const { t } = useTranslation('provider');
  const config = STATUS_ICON_CONFIG[statusFilter] ?? STATUS_ICON_CONFIG.ALL;
  const message = t(`bookings.emptyState.${range}.${statusFilter}`);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 32px 32px',
        textAlign: 'center',
      }}
    >
      {/* Decorative icon disc */}
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${config.color}14 0%, ${config.color}24 100%)`,
          border: `1px solid ${config.color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 30,
          color: config.color,
          marginBottom: 18,
          boxShadow: `0 8px 24px ${config.color}10`,
        }}
      >
        {config.icon}
      </div>

      <Text
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--bl-text-strong, #0f172a)',
          display: 'block',
          maxWidth: 280,
          lineHeight: 1.4,
        }}
      >
        {message}
      </Text>
    </div>
  );
}
